import { configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER, PersistConfig } from "redux-persist";
import storage from "redux-persist/lib/storage";
import globalReducer from "./slices/GlobalSlice";
import catalogueReducer from "./slices/catalogueSlice";
import itemsReducer from "./slices/ItemsSlice";
import saleDataReducer from "./slices/SaleSlice";
import customerReducer from "./slices/CustomerSlice";
import businessReducer from "./slices/BusinessSlice";
import discountReducer from "./slices/DiscountSlice";
import registerReducer from "./slices/RegisterSlice";
import loginReducer from "./slices/LoginSlice";
import tableViewReducer from "./slices/TableViewSlice";
import myOrderReducer from "./slices/MyOrderSlice";
// my-orders v2's DERIVED read model — filled by extraReducers on MyOrderSlice's own action
// types, so it has no writer of its own. Unpersisted, like `myOrderStore`.
import ordersV2Reducer from "../myOrders/slice";
import ItemsStatusReducer from "./slices/ItemStatusSlice";
import OutletStatusReducer from "./slices/OutletStatusSlice";
import LicenceExpirySlice from "./slices/LicenceExpirySlice";
// the payment ENGINE's own slice. Deliberately NOT in the persist whitelist below.
import { paymentEngineReducer } from "@rista/payments/engine/session";
import { orderingContextReducer } from "../orderingContext/slice";
import { channelPreferencesReducer } from "../channelPreferences/slice";
import { newOrderBrowseReducer } from "../orderTaking/newOrder/browse/slice";
import { promotionsReducer } from "../promotions/state/slice";
import { reservationsReducer } from "../reservations/state/slice";
import { tablesReducer } from "../tables/state/slice";
import createSagaMiddleware from "redux-saga";
import rootSaga from "./saga";
import logger from "redux-logger";
import checkoutReducer from "./slices/CheckoutSlice";
import cartReducer from "./slices/CartSlice";
import localPaymentInfo from "./slices/LocalPaymentInfo";
import LoyaltyReducer from "./slices/Loyalty";
import posSettingsReducer from "./slices/PosSettingsSlice";
import printerReducer from "./slices/PrinterSlice";
import RequestHeaderSlice from "./slices/RequestHeaderSlice";
import supportReducer from "./slices/SupportSlice";
import keyboardReducer from "./slices/KeyBoardShortcuts";
import saleSummaryReducer from "./slices/SaleSummarySlice";
import cashRegisterReducer from "./slices/CashRegisterSlice";
import localServerReducer from "./slices/LocalServerSlice";
import commonReducer from "./slices/common";
import notificationsReducer from "./slices/NotificationsSlice"
import InventorySlice from "./slices/InventorySlice";
import RecommenderSlice from "./slices/RecommenderSlice";
import notificationsListener from "./middleware/notificationsListener";
import globalLoaderReducer from "./slices/globalLoaderSlice";
import { Middleware } from "@reduxjs/toolkit";
import { combineReducers } from "redux";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
import { migrateOldStateToPersist } from "../utils/migrateState";
import saleUpdateMiddleware from "./middleware/saleUpdate";
import { isProdEnv } from "../data/envConstants";
// The kiosk slices sit with every other uplifted slice in ./slices, so both platforms register
// ONE definition. They are the only ones neither app ever had a copy of — they were born shared —
// which is why they are plain single-file folders with no variant to name.
import kioskReducer from "./slices/kioskSlice";
import kioskConfigReducer from "./slices/kioskConfigSlice";
import customerDisplayReducer from "./slices/CustomerDisplay";
import ncApprovalReducer from "./slices/NcApprovalSlice";
import { isCustomerDisplay } from "../utils/customerDisplay";

export * from "./shared";

/**
 * WEB variant of the store. See `./shared.ts` for the side-by-side comparison and for why there
 * is no shared body.
 *
 * ⚠️ Every intra-core import names the VARIANT rather than the folder (hazard 6/16). The one
 * exception is `./slices/globalLoaderSlice`, which is a single-platform folder holding a plain
 * `index.ts` — there is no variant to name.
 *
 * ⚠️ `migrateOldStateToPersist()` still runs at MODULE-EVAL time, before the store is built,
 * because redux-persist must rehydrate from already-migrated data on the first load. That
 * ordering is the reason the app's version of that function could never report its own failures;
 * see the note in `utils/migrateState/index.desktop.ts`.
 */

// The customer-display window shares localStorage with the POS window. It must
// not run the migration (two windows racing on the same keys) or start sagas
// (it has no auth token, so every request would 401).
//
// The app version of this line inlined the "/customer-display" literal to avoid an import cycle
// through commonHelpers. `utils/customerDisplay` is safe to name instead — it reaches only
// `customerDisplay` (pure), `utils/nativeHooks` and `data/constants`, none of which touch this
// store — and it keeps ONE definition of the predicate.
const isCustomerDisplayWindow = isCustomerDisplay();

// Migrate old manual localStorage state ("state" key) to redux-persist
// format ("persist:root" key) for users who haven't received the update yet.
// This runs synchronously before the store is created so redux-persist
// rehydrates from the already-migrated data on first load.
if (!isCustomerDisplayWindow) {
  migrateOldStateToPersist();
}

const rootReducer = combineReducers({
  globalStore: globalReducer,
  catalogStore: catalogueReducer,
  posSettingsStore: posSettingsReducer,
  itemsStore: itemsReducer,
  saleDataStore: saleDataReducer,
  customerStore: customerReducer,
  businessStore: businessReducer,
  discountStore: discountReducer,
  registerStore: registerReducer,
  loginStore: loginReducer,
  checkoutModalStore: checkoutReducer,
  cartStore: cartReducer,
  localPaymentInfoStore: localPaymentInfo,
  LoyaltyStore: LoyaltyReducer,
  tableViewStore: tableViewReducer,
  ItemsStatusStore: ItemsStatusReducer,
  OutletStatusStore: OutletStatusReducer,
  myOrderStore: myOrderReducer,
  ordersV2Store: ordersV2Reducer,
  printerStore: printerReducer,
  requestHeaderStore: RequestHeaderSlice,
  supportStore: supportReducer,
  keyboardShortcutStore: keyboardReducer,
  saleSummary: saleSummaryReducer,
  cashRegisterStore: cashRegisterReducer,
  localServer: localServerReducer,
  commonReducer: commonReducer,
  globalLoaderReducer: globalLoaderReducer,
  notificationsStore: notificationsReducer,
  inventoryStore: InventorySlice,
  recommenderStore: RecommenderSlice,
  // Deliberately absent from the persist whitelist below: a kiosk restarting mid-order
  // must come back to IDLE, not resume a stranger's cart.
  kioskStore: kioskReducer,
  // Merchant config, by contrast, IS persisted — the kiosk must boot and take orders on
  // a restaurant LAN with no internet. That split is why these are two slices.
  kioskConfigStore: kioskConfigReducer,
  customerDisplayStore: customerDisplayReducer,
  ncApprovalStore: ncApprovalReducer,
  LicenceExpirySlice,
  // the ENGINE's own slice — legs only, no sale-shaped state. Kept OUT of the
  // persist whitelist: a payment session must not survive a reload.
  paymentEngineStore: paymentEngineReducer,
  // which branch an order is for (NEW_ORDER_V2_PLAN.md §5). Kept OUT of the persist whitelist:
  // a reload starts in the session's branch, as the working sale starts empty.
  orderingContextStore: orderingContextReducer,
  // the operator's pinned channels per branch and module (CHANNEL_PREFERENCES.md); persisted, a device preference
  channelPreferencesStore: channelPreferencesReducer,
  newOrderBrowseStore: newOrderBrowseReducer,
  // discount v2 and loyalty v2's one store (DISCOUNT_V2_PLAN.md decisions 35, 39): approvals, loyalty
  // records, programmes. Kept OUT of the persist whitelist, as v1's discountStore and LoyaltyStore are.
  promotionsStore: promotionsReducer,
  // reservations v2's bookings, day lists, settings and partner outlets (RESERVATIONS_V2_PLAN.md §4.6, decision 54).
  // Kept OUT of the persist whitelist (a reload loads afresh) and emptied on sign-out.
  reservationsStore: reservationsReducer,
  // reservations v2's floors and live table states, per branch. Kept OUT of the persist whitelist and emptied on sign-out.
  tablesStore: tablesReducer,
});

type RootReducerType = ReturnType<typeof rootReducer>;

const persistConfig: PersistConfig<RootReducerType> = {
  key: "root",
  storage,
  version: 1,
  whitelist: [
    "globalStore",
    "catalogStore",
    "posSettingsStore",
    "customerStore",
    "registerStore",
    "loginStore",
    "ItemsStatusStore",
    "OutletStatusStore",
    "printerStore",
    "cashRegisterStore",
    "businessStore",
    "notificationsStore",
    "LicenceExpirySlice",
    "recommenderStore",
    "kioskConfigStore",
    "channelPreferencesStore",
    "newOrderBrowseStore",
  ],
  stateReconciler: autoMergeLevel2,
  throttle: 1000,
};

const persistedReducer = persistReducer<RootReducerType>(persistConfig, rootReducer);

const sagaMiddleware = createSagaMiddleware();
const middlewares: Middleware[] = [sagaMiddleware, saleUpdateMiddleware.middleware, notificationsListener.middleware];

if(!isProdEnv) {
  middlewares.push(logger as Middleware);
}

const store: any = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: false,
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(...middlewares)
});

export const persistor = persistStore(store);

let sagaStarted = false;

persistor.subscribe(() => {
  const { bootstrapped } = persistor.getState();

  if (bootstrapped && !sagaStarted && !isCustomerDisplayWindow) {
    sagaStarted = true;
    sagaMiddleware.run(rootSaga);
  }
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;

